import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Service } from '@shared/classes/service/service.class';
import { AlertService } from '../alert.service';
import { ModalService } from '../modal.service';

@Injectable()
export class AppointmentModalService {
  constructor(
    private modalService: ModalService,
    private alertService: AlertService,
    private translateService: TranslateService,
  ) {}

  public openInternalNoteModal(note: string) {
    return this.openNoteModal(note, 'appointment_internal_note');
  }

  public openExternalNoteModal(note: string) {
    return this.openNoteModal(note, 'appointment_external_note', true);
  }

  public async openColorModal(color: string): Promise<{ color?: string }> {
    const { ColorModalPage } = await import('@shared/modals/color-modal');
    const modal = await this.modalService.presentModal(ColorModalPage, { color });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    return data;
  }

  public async openCustomServiceModal(service: Service, isNew: boolean = false): Promise<{ service?: Service }> {
    const { CustomServiceModalPage } = await import('@shared/modals/custom-service-modal');
    const modal = await this.modalService.presentModal(CustomServiceModalPage, { activeService: service, isNew });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    return data;
  }

  public async openDatesModal(startedAt: string, endedAt: string): Promise<{ startedAt?: string }> {
    const { SelectStartDateModalPage } = await import('@shared/modals/select-start-date-modal');
    const modal = await this.modalService.presentModal(SelectStartDateModalPage, { startedAt, endedAt });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    return data;
  }

  public async showDeleteConfirmation(): Promise<{ isDelete: boolean }> {
    const deleteConfirm = await this.alertService.create({
      header: this.translateService.instant('appointment_delete_confirm_title'),
      message: this.translateService.instant('appointment_delete_confirm_msg'),
      buttons: [
        {
          text: this.translateService.instant('common_cancel'),
          role: 'cancel',
        },
        {
          text: this.translateService.instant('common_delete'),
          role: 'delete',
        },
      ],
    });
    await deleteConfirm.present();
    const { role } = await deleteConfirm.onDidDismiss();
    return role === 'delete' ? { isDelete: true } : null;
  }

  public async showCancelConfirmation(hasCustomer: boolean): Promise<any> {
    const { CancelAppointmentModalPage } = await import('@shared/modals/cancel-appointment-modal');
    const modal = await this.modalService.presentModal(CancelAppointmentModalPage, { hasCustomer });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    return data;
  }

  private async openNoteModal(note: string, title: string, isDisabledEdit: boolean = false): Promise<{ note?: string }> {
    const { AppointmentNoteModalPage } = await import('@shared/modals/appointment-note-modal');
    const modal = await this.modalService.presentModal(AppointmentNoteModalPage, { note, title, isDisabledEdit });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    return data;
  }
}
